import {Component, OnInit, signal, ViewChild} from '@angular/core';
import {CodaltComponent} from '../../codalt.component';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {User} from '../../classes/user.class';
import {Settings} from '../../settings.class';
import {LocalStorage} from '../../storage.class';
import {Observable} from 'rxjs';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmDialogService} from '../../services/confirm-dialog-service/confirm-dialog.service';
import {CameraService} from '../../camera.service';
import {CordovaService} from '../../cordova.service';
import {AuthenticationService} from '../../services/auth/authentication.service';
import {requiredConditional} from '../../validators/required-conditional.validator';
import {AuthorisationService} from '../../services/auth/authorisation.service';
import {Routenames} from '../../route-names.enum';
import {DatePipe, formatDate} from '@angular/common';
import {RepairFile} from '../../repairs/edit-repair/edit-repair.component';
import {PushpinPartImageViewerDialogComponent} from '../../pushpin-detail/pushpin-part-image-viewer-dialog/pushpin-part-image-viewer-dialog.component';
import {Utils} from '../../utils.class';
import {UploadFile} from '../../import/upload-file';
import {SmallMaterialRequest} from '../../smallmaterial-request';
import {SmallMaterial} from '../../smallmaterial';
import {SmallMaterialStatus} from '../../smallmaterial-status';
import {SmallMaterialService} from '../../services/small-material.service';
import {VvrIconComponent} from "../../vvr-icon/vvr-icon.component";
import {MatTooltip} from "@angular/material/tooltip";
import {MatError, MatFormField, MatInput, MatLabel} from "@angular/material/input";
import {MatOption, MatSelect} from "@angular/material/select";
import {CdkTextareaAutosize} from "@angular/cdk/text-field";
import {SafeBgUrlPipe} from "../../pipes/safe-bg-url.pipe";
import {MatList, MatListItem, MatListSubheaderCssMatStyler} from "@angular/material/list";
import {MatLine} from "@angular/material/core";

declare var cordova;

@Component({
    selector: 'app-small-material-request-detail',
    templateUrl: './small-material-request-detail.component.html',
    styleUrls: ['./small-material-request-detail.component.scss'],
    imports: [
        VvrIconComponent,
        RouterLink,
        MatTooltip,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatSelect,
        MatOption,
        CdkTextareaAutosize,
        MatInput,
        MatError,
        SafeBgUrlPipe,
        MatList,
        MatListItem,
        MatListSubheaderCssMatStyler,
        MatLine,
        DatePipe
    ]
})
export class SmallMaterialRequestDetailComponent extends CodaltComponent implements OnInit {

    @ViewChild('scrollviewport') upload;

    readonly saving = signal<boolean>(false);
    readonly uploading = signal<boolean>(false);

    fc: {
        status_id: FormControl,
        smallmaterial_id: FormControl,
        manager_id: FormControl,
        comment: FormControl,
        reason: FormControl
    };

    readonly form = signal<FormGroup>(null);

    request: SmallMaterialRequest;

    readonly equipmentList = signal<SmallMaterial[]>([]);
    statusList: SmallMaterialStatus[];
    managerList: User[];

    url = Settings.API_ENDPOINT + 'file/';
    token = LocalStorage.getUserToken();

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private dialog: MatDialog,
        private confirmDialog: ConfirmDialogService,
        private cameraService: CameraService,
        private cordovaService: CordovaService,
        private authenticationService: AuthenticationService,
        private smallMaterialService: SmallMaterialService
    ) {
        super();
    }

    ngOnInit(): void {
        this.isCordova = typeof cordova !== 'undefined';
        this.subscriptions.add(this.authenticationService.onLogin.subscribe(() => {
            this.token = LocalStorage.getUserToken();
        }));
        this.subscriptions.add(this.route.params.subscribe(params => {
            this.smallMaterialService.getRequest(params['id']).subscribe(equipment => {
                this.request = equipment.data || new SmallMaterialRequest();
                this.fc = {
                    status_id: new FormControl(this.request.status_id || 1, Validators.required),
                    smallmaterial_id: new FormControl(this.request.smallmaterial_id, Validators.required),
                    manager_id: new FormControl(this.request.manager_id, Validators.required),
                    comment: new FormControl(this.request.comment, Validators.required),
                    reason: new FormControl(this.request.reason, requiredConditional(() => this.fc?.status_id?.value === 3))
                };
                if (!AuthorisationService.hasFeature('smallMaterialManagement')) {
                    this.fc.status_id.disable();
                    if (this.request.status_id > 1) {
                        this.fc.smallmaterial_id.disable();
                        this.fc.manager_id.disable();
                        this.fc.comment.disable();
                        this.fc.reason.disable();
                    }
                }
                this.form.set(new FormGroup(this.fc));
                this.smallMaterialService.listMaterials().subscribe(equipmentData => {
                    this.equipmentList.set(equipmentData.data);
                    if (this.request.smallmaterial_id && !this.equipmentList().find(e => e.id === this.request.smallmaterial_id)) {
                        this.equipmentList.update(list => [...list, this.request.smallmaterial]);
                    }
                });
            });
        }));


        this.smallMaterialService.statusList().subscribe(status => {
            this.statusList = status.data;
        });
        this.smallMaterialService.managerList().subscribe(managers => {
            this.managerList = managers.data;
        });
    }

    save() {
        if (!this.request.images?.length && this.request.status_id === 1) {
            this.confirmDialog.confirm(
                'Geen foto toegevoegd',
                `Er is geen foto toegevoegd. Wilt u alsnog een foto toevoegen?`,
                'Ja, toevoegen',
                'Nee, opslaan').then(() => {
                this.takePicture();
            }, () => {
                this.saveRequest();
            });
        } else {
            this.saveRequest();
        }
    }

    delete() {
        this.confirmDialog.confirm(
            'Aanvraag intrekken',
            `Weet u zeker dat u deze aanvraag wilt intrekken?`,
            'Intrekken',
            'Behouden'
        ).then(() => {
            this.smallMaterialService.deleteRequest(this.request.id).subscribe(() => {
                this.router.navigate([Routenames.smallMaterialRequest]);
            });
        }, () => {

        });
    }

    public uploadFile(event) {
        const files = event.srcElement.files;
        this.uploadFiles(files);
    }

    takePicture() {
        if (this.isCordova) {
            this.cameraService.getPicture().then(pictureUrl => {
                this.addFile(pictureUrl, formatDate(new Date(), 'dd-mm-yyyy hh:mm:ss', 'nl'), 'jpg');
            }, error => {
                console.error(error);
            });
        }
    }

    addFile(fileUrl, name, ext, object?: SmallMaterialRequest) {
        if (!object) {
            object = this.request;
        }
        if (!Array.isArray(object.images)) {
            object.images = [];
        }
        const file = new RepairFile();
        file.name = name;
        file.file = fileUrl;
        file.ext = ext;
        object.images.push(file);
        if (typeof object['id'] !== 'undefined') {
            this.smallMaterialService.saveRequest(object as SmallMaterialRequest).subscribe();
        }
    }

    openFile(clickedImage, allFiles) {
        if (['jpg', 'jpeg', 'png'].indexOf(clickedImage.ext) !== -1) {
            const images = allFiles
                .filter(f => ['jpg', 'jpeg', 'png'].indexOf(f.ext) !== -1)
                .map(f => (f.file.substr(0, 4) !== 'http' ? this.url : '') + f.file + '?access_token=' + this.token);
            const dialogRef = this.dialog.open(PushpinPartImageViewerDialogComponent, {
                panelClass: 'image-viewer-dialog',
                data: {
                    images: images,
                    viewIndex: allFiles.filter(f => ['jpg', 'jpeg', 'png'].indexOf(f.ext) !== -1).indexOf(clickedImage)
                }
            });
            this.cordovaService.setBackbuttonAction(() => {
                dialogRef.close();
            });
            const subs = dialogRef.afterClosed().subscribe(() => {
                this.cordovaService.setBackbuttonAction(() => {
                    this.router.navigateByUrl('/repairs');
                });
                subs.unsubscribe();
            });
        } else {
            window.open(this.url + clickedImage.file + '?access_token=' + this.token);
        }
    }

    private saveRequest() {
        Utils.triggerValidation(this.form());
        if (this.form().valid) {
            this.request.comment = this.fc.comment.value;
            this.request.reason = this.fc.reason.value;
            this.request.status_id = this.fc.status_id.value;
            this.request.smallmaterial_id = this.fc.smallmaterial_id.value;
            this.request.manager_id = this.fc.manager_id.value;
            this.smallMaterialService.saveRequest(this.request).subscribe(() => {
                this.router.navigate([Routenames.smallMaterialRequest]);
            }, error => {

            });
        }
    }

    private uploadFiles(files: File[], object?: SmallMaterialRequest) {
        this.uploading.set(true);
        const uploadedFiles = [];
        const url = `${Settings.API_ENDPOINT}images`;
        if (files.length > 0) {
            for (let i = 0; i < files.length; i++) {
                const uploadFile = new UploadFile();
                uploadFile.filesize = Math.round(files[i].size / 1000);
                uploadFile.file = files[i].name;
                uploadFile.ext = files[i].type;
                if (['jpg', 'jpeg', 'image/jpeg', 'doc', 'docx', 'xls', 'xlsx', 'pdf', 'image/png', 'png', 'application/pdf',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/msword']
                    .indexOf(uploadFile.ext.toLowerCase()) !== -1 && files[i].size < 110664300) {
                    this.makeFileRequest(url, files[i], uploadFile).subscribe(data => {
                        if (data['success']) {
                            this.addFile(data['file'], data['name'], data['ext'], object);
                            uploadFile['uploading'] = null;
                            uploadFile.ext = data['ext'];
                        } else {
                            let error = 'Een onbekende fout';
                            if (typeof data['error'] === 'object' && Array.isArray(data['error']['upload'])) {
                                error = data['error']['upload'][0];
                            } else if (typeof data['error'] === 'string') {
                                error = data['error'];
                            }
                            this.confirmDialog.confirm(
                                'Er is iets fout gegaan met uploaden',
                                error,
                                'Oké', null);
                        }
                        uploadedFiles.push(uploadFile);
                        if (uploadedFiles.length === files.length) {
                            this.uploading.set(false);
                        }
                    }, error => {
                        this.confirmDialog.confirm(
                            'Er is iets fout gegaan met uploaden',
                            'Een onbekende fout',
                            'Oké', null);
                        this.uploading.set(false);
                    });
                } else if (['jpg', 'jpeg', 'image/jpeg'].indexOf(uploadFile.ext) === -1) {
                    this.uploading.set(false);
                    this.confirmDialog.confirm(
                        'U kunt alleen documenten uploaden',
                        'U kunt alleen jpg, doc(x), xls(x) en pdf bestanden uploaden',
                        'Oké', null);
                } else {
                    this.uploading.set(false);
                    this.confirmDialog.confirm(
                        'Het bestand is te groot',
                        'Het bestand is groter dan 100Mb',
                        'Oké', null);
                }
            }
        }
    }

    private makeFileRequest(url: string, file: File, uploadFile: UploadFile): Observable<number> {
        return Observable.create(observer => {
            const formData: FormData = new FormData(),
                xhr: XMLHttpRequest = new XMLHttpRequest();

            formData.append('upload', file, file.name);
            formData.append('type', 'file');

            xhr.onreadystatechange = () => {
                if (xhr.readyState === 4) {
                    if (xhr.status === 200) {
                        observer.next(JSON.parse(xhr.response));
                        observer.complete();
                    } else {
                        observer.error(xhr.response);
                    }
                }
            };

            xhr.upload.onprogress = (event) => {
                uploadFile['uploading'] = Math.round(event.loaded / event.total * 100);

            };
            xhr.open('POST', url, true);
            xhr.setRequestHeader('Accept', 'application/json');
            xhr.setRequestHeader('Authorization', 'Bearer ' + LocalStorage.getUserToken());
            xhr.send(formData);
        });
    }

}
